import { Alert, App as AntApp, Button, Card, DatePicker, Form, Select, Space, Table, Tag } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useReportError } from "../app/students.ts";

type LivingOption = components["schemas"]["LivingOption"];
type LivingOptionPeriod = components["schemas"]["LivingOptionPeriodDetails"];

const options: LivingOption[] = ["Boarding", "Day"];
const fields = ["option", "from"] as const;

interface StartForm {
  option?: LivingOption;
  from?: Dayjs;
}

export function StudentLivingTab({ studentId, canManage }: { studentId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<StartForm>();
  const reportError = useReportError(form, fields);

  const history = useQuery({
    queryKey: ["students", studentId, "living-options"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/students/{id}/living-options", { params: { path: { id: studentId } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });

  const start = useMutation({
    mutationFn: async (values: Required<StartForm>) => {
      const { data, error, response } = await api.POST("/students/{id}/living-options", {
        params: { path: { id: studentId } },
        body: { option: values.option, from: values.from.format("YYYY-MM-DD") },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (periods) => {
      queryClient.setQueryData(["students", studentId, "living-options"], periods);
      await queryClient.invalidateQueries({ queryKey: ["students", studentId], exact: true });
      form.resetFields();
      void message.success(t("student.living.started"));
    },
    onError: reportError,
  });

  const open = history.data?.find((p) => p.to == null);
  const required = { required: true, message: t("family.validation.required") };
  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      {history.isError ? (
        <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void history.refetch()}>{t("app.retry")}</Button>} />
      ) : (
        <Table<LivingOptionPeriod>
          rowKey="id"
          size="small"
          loading={history.isPending}
          dataSource={history.data ?? []}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: t("student.living.empty") }}
          columns={[
            { key: "option", title: t("student.living.option"), render: (_, p) => <Tag color={p.option === "Boarding" ? "purple" : "cyan"}>{t(`student.livingOptions.${p.option}`)}</Tag> },
            { key: "from", title: t("student.living.from"), render: (_, p) => dayjs(p.from).format("DD.MM.YYYY") },
            { key: "to", title: t("student.living.to"), render: (_, p) => (p.to ? dayjs(p.to).format("DD.MM.YYYY") : t("student.living.ongoing")) },
          ]}
        />
      )}

      {canManage && (
        <Card size="small" title={t("student.living.start")} style={{ maxWidth: 640 }}>
          <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("student.living.startHint")} />
          <Form<StartForm>
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={(values) => {
              if (values.option && values.from) start.mutate({ option: values.option, from: values.from });
            }}
          >
            <Form.Item name="option" label={t("student.living.option")} rules={[required]}>
              <Select options={options.filter((o) => o !== open?.option).map((o) => ({ value: o, label: t(`student.livingOptions.${o}`) }))} />
            </Form.Item>
            <Form.Item name="from" label={t("student.living.from")} rules={[required]}>
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" disabledDate={(d) => (open ? !d.isAfter(dayjs(open.from), "day") : false)} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={start.isPending}>
              {t("student.living.submit")}
            </Button>
          </Form>
        </Card>
      )}
    </Space>
  );
}
